'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from '@/i18n/navigation';
import { useGiftVoucherStore, VALUE_VOUCHER_MIN_RON, VALUE_VOUCHER_DEFAULT_RON } from '@/stores/gift-voucher-store';

export function ValueVoucherForm() {
  const t = useTranslations('giftVouchers');
  const router = useRouter();
  const setDraft = useGiftVoucherStore((s) => s.setDraft);
  // Raw string state so the field can be cleared / typed through freely.
  // The min is enforced on blur + on continue, NOT on every keystroke —
  // clamping mid-typing makes "delete 700, type 1500" impossible.
  const [valueInput, setValueInput] = useState(String(VALUE_VOUCHER_DEFAULT_RON));

  const parsed = parseInt(valueInput, 10);
  const effectiveRon = Number.isNaN(parsed) ? 0 : parsed;
  const belowMin = effectiveRon > 0 && effectiveRon < VALUE_VOUCHER_MIN_RON;

  const clampToMin = () => {
    const clamped = effectiveRon < VALUE_VOUCHER_MIN_RON ? VALUE_VOUCHER_MIN_RON : effectiveRon;
    setValueInput(String(clamped));
    return clamped;
  };

  const handleContinue = () => {
    const valueRon = clampToMin();
    setDraft({ type: 'value', valueRon });
    router.push('/gift-vouchers/checkout');
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-[#2c1810] mb-3">{t('flexible.valueLabel')}</label>
        <div className="relative">
          <input
            type="number"
            inputMode="numeric"
            min={VALUE_VOUCHER_MIN_RON}
            step={50}
            value={valueInput}
            onChange={(e) => setValueInput(e.target.value)}
            onBlur={clampToMin}
            className="w-full px-5 py-4 text-2xl font-semibold text-[#2c1810] bg-[#faf8f6] border border-gray-200 rounded-2xl focus:ring-2 focus:ring-[#801515]/30 focus:border-[#801515] outline-none transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0"
          />
          <span className="absolute right-5 top-1/2 -translate-y-1/2 text-lg font-semibold text-gray-400">RON</span>
        </div>
        <p className={`text-xs mt-2 ${belowMin ? 'text-[#801515]' : 'text-gray-400'}`}>{t('flexible.minValue')}</p>
      </div>

      <div className="flex justify-between items-center pt-4 border-t border-gray-100">
        <span className="text-sm text-gray-500">{t('total')}</span>
        <span className="text-3xl font-bold text-[#801515] shrink-0">{effectiveRon.toLocaleString()} RON</span>
      </div>

      <button
        onClick={handleContinue}
        className="w-full py-4 rounded-2xl bg-[#801515] text-white font-semibold text-lg hover:bg-[#601010] active:scale-[0.98] transition-all duration-200 shadow-lg shadow-[#801515]/25 cursor-pointer"
      >
        {t('proceedToPayment')}
      </button>
    </div>
  );
}
